<script lang="ts" setup>
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import DragHandle from '@/corePagesModule/components/DragHandle.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import OrderStatusBadge from '@/corePagesModule/components/OrderStatusBadge.vue';
import { useNativeDragReorder } from '@/corePagesModule/composables/useNativeDragReorder';
import { i18n } from '@/i18n';
import OrderStatusFormSelectInput from '@/unmatchedOrderModule/components/OrderStatusFormSelectInput.vue';
import { ref, watch } from 'vue';
import { ConditionOperators, ConditionValueTypes } from '@/coreDomainsModule/entities/InputOrderExcelInfo';
import type {
  ConditionOperator,
  ConditionValueType,
  ImportOrderStatusCondition,
  ImportOrderStatusRule,
} from '@/coreDomainsModule/entities/InputOrderExcelInfo';
import SelectableField from '../SelectableField.vue';
import ConditionDateInput from './ConditionDateInput.vue';

const t = i18n.t;
const DEFAULT_ORDER_STATUS = OrderStatus.ApprovedAwaitingDeliveryDate;
const RULE_ID_PREFIX = 'ROS';
const CONDITION_ID_PREFIX = 'COS';
const RANDOM_ID_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const FALLBACK_STATUS_SELECT_INDEX = 999;
const DRAG_HANDLE_SELECTOR = '.drag-handle';

const SELECTABLE_ORDER_STATUS = [
  OrderStatus.ApprovedAwaitingDeliveryDate,
  OrderStatus.QuantityConfirmed,
  OrderStatus.DeliveryDateConfirmed,
  OrderStatus.Confirmed,
  OrderStatus.Delivered,
  OrderStatus.DeliveryDelayed,
];

const props = defineProps<{
  suggestions: string[];
  rules: ImportOrderStatusRule[];
  fallbackStatus: OrderStatus;
  isEditing: boolean;
}>();

const emits = defineEmits<{
  (e: 'update:rules', value: ImportOrderStatusRule[]): void;
  (e: 'update:fallbackStatus', value: OrderStatus): void;
}>();

const conditionValueTypeLabels: Record<Exclude<ConditionValueType, 'none'>, string> = {
  text: t('文字列'),
  number: t('数値'),
  date: t('日付'),
};

const conditionOperatorLabels: Record<ConditionOperator, string> = Object.fromEntries(
  ConditionOperators.map((operator) => [operator, t(`condition-op-${operator}`)])
) as Record<ConditionOperator, string>;

const conditionOperatorSuggestions = ConditionOperators.map((operator) => conditionOperatorLabels[operator]);
const conditionValueTypeSuggestions = ConditionValueTypes.map((valueType) => conditionValueTypeLabels[valueType]);
const comparableConditionValueTypeSuggestions = ConditionValueTypes.filter((valueType) => valueType !== 'text').map(
  (valueType) => conditionValueTypeLabels[valueType]
);
const containsConditionValueTypeSuggestions = ConditionValueTypes.filter((valueType) => valueType !== 'date').map(
  (valueType) => conditionValueTypeLabels[valueType]
);

/**
 * 大小比較を行う条件かどうかを判定する
 * @param operator - 条件演算子
 * @returns 大小比較を行う場合true
 */
const isComparableOperator = (operator: ConditionOperator): boolean => ['gt', 'gte', 'lt', 'lte'].includes(operator);

/**
 * 部分一致を行う条件かどうかを判定する
 * @param operator - 条件演算子
 * @returns 部分一致を行う場合true
 */
const isContainsOperator = (operator: ConditionOperator): boolean => ['contains', 'not_contains'].includes(operator);

/**
 * 条件演算子に応じた入力種別候補を取得する
 * @param operator - 条件演算子
 * @returns 条件値の入力種別候補
 */
const getConditionValueTypeSuggestions = (operator: ConditionOperator): string[] => {
  if (isComparableOperator(operator)) return comparableConditionValueTypeSuggestions;
  if (isContainsOperator(operator)) return containsConditionValueTypeSuggestions;
  return conditionValueTypeSuggestions;
};

/**
 * RuleとConditionへのID付与のためのランダム文字列を取得する
 * @param length - 文字列の長さ
 */
const generateRandomString = (length = 16): string => {
  const values = new Uint32Array(length);

  crypto.getRandomValues(values);

  return Array.from(values, (value) => RANDOM_ID_CHARS[value % RANDOM_ID_CHARS.length]).join('');
};

/**
 * デフォルトのRuleを取得
 */
const createDefaultRule = (): ImportOrderStatusRule => ({
  id: RULE_ID_PREFIX + generateRandomString(),
  conditions: [createDefaultCondition()],
  result: DEFAULT_ORDER_STATUS,
});

/**
 * デフォルトのConditionを取得
 */
const createDefaultCondition = (): ImportOrderStatusCondition => ({
  id: CONDITION_ID_PREFIX + generateRandomString(),
  header: '',
  operator: 'equals',
  valueType: 'text',
  value: '',
  validateError: {
    header: 'none',
    value: 'none',
  },
});

/**
 * ルールをpropsと切り離して複製する
 * @param rules - 複製元のルール
 * @returns 複製したルール
 */
const cloneRules = (rules: ImportOrderStatusRule[]): ImportOrderStatusRule[] =>
  rules.map((rule) => ({
    ...rule,
    conditions: rule.conditions.map((condition) => {
      const shouldNormalizeComparableValue = isComparableOperator(condition.operator) && condition.valueType === 'text';
      const shouldNormalizeContainsValue = isContainsOperator(condition.operator) && condition.valueType === 'date';

      return {
        ...condition,
        valueType: shouldNormalizeComparableValue ? 'number' : shouldNormalizeContainsValue ? 'text' : condition.valueType,
        value: shouldNormalizeComparableValue ? null : shouldNormalizeContainsValue ? String(condition.value ?? '') : condition.value,
        validateError: condition.validateError ?? {
          header: 'none',
          value: 'none',
        },
      };
    }),
  }));

const rulesForDrag = ref<ImportOrderStatusRule[]>(cloneRules(props.rules));

watch(
  () => props.rules,
  (rules) => {
    rulesForDrag.value = cloneRules(rules);
  },
  { deep: true }
);

/**
 * ルール全体の変更を親へ通知する
 * @param rules - 更新後のルール
 */
const emitRules = (rules: ImportOrderStatusRule[]): void => {
  const nextRules = cloneRules(rules);
  rulesForDrag.value = nextRules;
  emits('update:rules', cloneRules(nextRules));
};

/**
 * 演算子の表示値を取得する
 * @param operator - 変換する演算子
 */
const getOperatorDisplayValue = (operator: ConditionOperator): string => conditionOperatorLabels[operator];

/**
 * 条件演算子の表示名から内部値を取得する
 * @param displayValue - 条件演算子の表示名
 * @returns 条件演算子
 */
const getOperatorByDisplayValue = (displayValue: string): ConditionOperator => {
  return ConditionOperators.find((operator) => conditionOperatorLabels[operator] === displayValue) ?? 'equals';
};

/**
 * 値入力が不要な条件かどうかを判定する
 * @param operator - 条件演算子
 * @returns 値入力が不要な場合true
 */
const isNoValueOperator = (operator: ConditionOperator): boolean => operator === 'is_empty' || operator === 'is_not_empty';

/**
 * 条件値の入力種別表示名を取得する
 * @param valueType - 条件値の入力種別
 * @returns 表示名
 */
const getValueTypeDisplayValue = (valueType: ConditionValueType): string => {
  if (valueType === 'none') return '';
  return conditionValueTypeLabels[valueType];
};

/**
 * 条件値の入力種別表示名から内部値を取得する
 * @param displayValue - 表示名
 * @returns 条件値の入力種別
 */
const getValueTypeByDisplayValue = (displayValue: string): Exclude<ConditionValueType, 'none'> => {
  return ConditionValueTypes.find((valueType) => conditionValueTypeLabels[valueType] === displayValue) ?? 'text';
};

/**
 * 条件値の表示文字列を取得する
 * @param condition - 条件
 * @returns 条件値の表示文字列
 */
const getConditionValueDisplayValue = (condition: ImportOrderStatusCondition): string => {
  if (isNoValueOperator(condition.operator)) return '--';
  if (condition.value == null) return '';
  return String(condition.value);
};

/**
 * 条件値の入力エラー文言を取得する
 * @param condition - 条件
 * @returns 条件値の入力エラー文言
 */
const getConditionValueErrorMessage = (condition: ImportOrderStatusCondition): string => {
  if (condition.validateError.value === 'notEmpty') return t('記入必須です');
  return '　';
};

/**
 * 新規ルールを追加
 */
const addRule = (): void => {
  if (!props.isEditing) return;

  emitRules([...rulesForDrag.value, createDefaultRule()]);
};

/**
 * 新規コンディションを追加
 * @param ruleIndex - 親ルールのIndex
 */
const addCondition = (ruleIndex: number): void => {
  if (!props.isEditing) return;

  emitRules(
    rulesForDrag.value.map((rule, index) =>
      index === ruleIndex ? { ...rule, conditions: [...rule.conditions, createDefaultCondition()] } : rule
    )
  );
};

/**
 * ルールを削除
 * @param index - 削除するルールのIndex
 */
const removeRule = (index: number): void => {
  if (!props.isEditing) return;

  emitRules(rulesForDrag.value.filter((_, ruleIndex) => ruleIndex !== index));
};

/**
 * コンディションの削除
 * @param ruleIndex - 親ルールのIndex
 * @param conditionIndex - 削除するConditionのIndex
 */
const removeCondition = (ruleIndex: number, conditionIndex: number): void => {
  if (!props.isEditing) return;

  const nextRules = rulesForDrag.value.flatMap((rule, index) => {
    if (index !== ruleIndex) return [rule];

    const conditions = rule.conditions.filter((_, index) => index !== conditionIndex);
    return conditions.length === 0 ? [] : [{ ...rule, conditions }];
  });

  emitRules(nextRules);
};

/**
 * 条件を差し替える
 * @param ruleIndex - 更新対象のルールindex
 * @param conditionIndex - 更新対象の条件index
 * @param condition - 差し替える条件
 */
const updateCondition = (ruleIndex: number, conditionIndex: number, condition: ImportOrderStatusCondition): void => {
  if (!props.isEditing) return;

  emitRules(
    rulesForDrag.value.map((rule, index) =>
      index === ruleIndex
        ? {
            ...rule,
            conditions: rule.conditions.map((currentCondition, index) => (index === conditionIndex ? condition : currentCondition)),
          }
        : rule
    )
  );
};

/**
 * 条件を部分更新する
 * @param ruleIndex - 更新対象のルールindex
 * @param conditionIndex - 更新対象の条件index
 * @param patch - 更新する値
 */
const updateConditionByIndex = (ruleIndex: number, conditionIndex: number, patch: Partial<ImportOrderStatusCondition>): void => {
  if (!props.isEditing) return;

  const currentCondition = rulesForDrag.value[ruleIndex]?.conditions[conditionIndex];
  if (!currentCondition) return;

  updateCondition(ruleIndex, conditionIndex, {
    ...currentCondition,
    ...patch,
    validateError: {
      ...currentCondition.validateError,
      ...patch.validateError,
      header: patch.header != null ? 'none' : currentCondition.validateError.header,
      value: patch.value !== undefined ? 'none' : currentCondition.validateError.value,
    },
  });
};

/**
 * 条件演算子を更新する
 * @param ruleIndex - 更新対象のルールindex
 * @param conditionIndex - 更新対象の条件index
 * @param operator - 条件演算子
 */
const updateConditionOperator = (ruleIndex: number, conditionIndex: number, operator: ConditionOperator): void => {
  if (!props.isEditing) return;

  const currentCondition = rulesForDrag.value[ruleIndex]?.conditions[conditionIndex];
  if (!currentCondition) return;

  const condition: ImportOrderStatusCondition = {
    ...currentCondition,
    operator,
    validateError: {
      ...currentCondition.validateError,
      value: 'none',
    },
  };

  if (isNoValueOperator(operator)) {
    condition.valueType = 'none';
    condition.value = null;
  } else if (condition.valueType === 'none') {
    condition.valueType = 'text';
    condition.value = '';
  } else if (isComparableOperator(operator) && condition.valueType === 'text') {
    condition.valueType = 'number';
    condition.value = null;
  } else if (isContainsOperator(operator) && condition.valueType === 'date') {
    condition.valueType = 'text';
    condition.value = String(condition.value ?? '');
  }

  updateCondition(ruleIndex, conditionIndex, condition);
};

/**
 * 条件値の入力種別を更新する
 * @param ruleIndex - 更新対象のルールindex
 * @param conditionIndex - 更新対象の条件index
 * @param displayValue - 入力種別の表示名
 */
const updateConditionValueType = (ruleIndex: number, conditionIndex: number, displayValue: string): void => {
  if (!props.isEditing) return;

  const valueType = getValueTypeByDisplayValue(displayValue);
  updateConditionByIndex(ruleIndex, conditionIndex, {
    valueType,
    value: valueType === 'number' ? null : '',
    validateError: {
      ...rulesForDrag.value[ruleIndex].conditions[conditionIndex].validateError,
      value: 'none',
    },
  });
};

/**
 * 数値条件値を更新する
 * @param ruleIndex - 更新対象のルールindex
 * @param conditionIndex - 更新対象の条件index
 * @param value - 入力値
 */
const updateNumberConditionValue = (ruleIndex: number, conditionIndex: number, value: string): void => {
  if (!props.isEditing) return;

  updateConditionByIndex(ruleIndex, conditionIndex, {
    value: value === '' ? null : Number(value),
  });
};

/**
 * 登録値を更新する
 * @param ruleIndex - 更新対象のルールindex
 * @param result - 登録値
 */
const updateRuleResult = (ruleIndex: number, result: OrderStatus): void => {
  if (!props.isEditing) return;

  emitRules(rulesForDrag.value.map((rule, index) => (index === ruleIndex ? { ...rule, result } : rule)));
};

/**
 * 条件に一致しない場合の発注ステータスを更新する
 * @param status - 発注ステータス
 */
const updateFallbackStatus = (status: OrderStatus): void => {
  if (!props.isEditing) return;

  emits('update:fallbackStatus', status);
};

/**
 * 発注ステータス条件のDnDキーを取得する
 * @param item - 発注ステータス条件
 * @returns 条件ID
 */
const getRuleKey = (item: ImportOrderStatusRule): string => item.id;

/**
 * DnDで並び替えたルールを親へ通知する
 */
const handleRulesReorder = (): void => {
  if (!props.isEditing) return;

  emitRules(rulesForDrag.value);
};

const {
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragLeave,
  handleDragOverEnd,
  handleDragLeaveEnd,
  handleDrop,
  handleDropToEnd,
  isDragging,
  isDropTarget,
  isDragOverEnd,
} = useNativeDragReorder({
  items: rulesForDrag,
  getKey: getRuleKey,
  onReorder: handleRulesReorder,
});

const canStartRuleDrag = ref(false);

/**
 * ドラッグ開始前に、操作開始位置がハンドルかどうかを記録する
 * @param event - pointerdownイベント
 */
const prepareRuleDrag = (event: PointerEvent): void => {
  if (!props.isEditing) {
    canStartRuleDrag.value = false;
    return;
  }

  const target = event.target;
  canStartRuleDrag.value = target instanceof HTMLElement && Boolean(target.closest(DRAG_HANDLE_SELECTOR));
};

/**
 * ハンドルから開始した時だけ行ドラッグを許可する
 * @param event - dragstartイベント
 * @param item - ドラッグする条件
 */
const handleRuleDragStart = (event: DragEvent, item: ImportOrderStatusRule): void => {
  if (!props.isEditing) {
    event.preventDefault();
    return;
  }

  if (!canStartRuleDrag.value) {
    event.preventDefault();
    return;
  }

  handleDragStart(event, item);
  canStartRuleDrag.value = false;
};

/**
 * ドラッグ終了時にDnD状態を戻す
 */
const handleRuleDragEnd = (): void => {
  canStartRuleDrag.value = false;
  handleDragEnd();
};
</script>

<template>
  <div class="order-status-info">
    <h3 class="order-status-info__title">{{ t('発注ステータスの設定') }}</h3>

    <div class="order-status-rules">
      <div class="order-status-rules__rule order-status-rules__rule--header">
        <div />
        <div class="order-status-rules__rule-layout">
          <div class="order-status-rules__condition-row order-status-rules__condition-row--header">
            <div />
            <div>
              {{ t('登録ファイル項目') }}
            </div>
            <div>
              {{ t('条件') }}
            </div>
            <div>
              {{ t('セル値') }}
            </div>
            <div />
          </div>
          <div class="order-status-rules__result-header">
            {{ t('登録値') }}
          </div>
          <div class="order-status-rules__actions order-status-rules__actions--header" />
        </div>
      </div>

      <div
        v-for="(rule, ruleIndex) in rulesForDrag"
        :key="rule.id"
        class="order-status-rules__rule"
        :class="{
          'order-status-rules__rule--dragging': isDragging(rule),
          'order-status-rules__rule--drop-target': isDropTarget(rule),
        }"
        :draggable="isEditing"
        @pointerdown="prepareRuleDrag"
        @dragstart="handleRuleDragStart($event, rule)"
        @dragend="handleRuleDragEnd"
        @dragover="handleDragOver($event, rule)"
        @dragleave="handleDragLeave($event, rule)"
        @drop="handleDrop($event, rule)"
      >
        <DragHandle v-if="isEditing" />
        <div v-else />
        <div class="order-status-rules__rule-layout">
          <div class="order-status-rules__conditions">
            <div v-for="(condition, conditionIndex) in rule.conditions" :key="condition.id" class="order-status-rules__condition-row">
              <div class="order-status-rules__join-label">
                {{ conditionIndex === 0 ? '' : t('かつ') }}
              </div>

              <div class="order-status-rules__select-input">
                <SelectableField
                  v-if="isEditing"
                  :value="condition.header || undefined"
                  :selected-id="condition.header"
                  :hint="t('項目を選択')"
                  :suggestions="suggestions"
                  @update-value="(e) => updateConditionByIndex(ruleIndex, conditionIndex, { header: e })"
                />
                <span v-else class="order-status-rules__readonly-text">{{ condition.header }}</span>
                <span v-if="isEditing" class="order-status-rules__error-text">{{
                  condition.validateError.header === 'notEmpty' ? t('記入必須です') : '　'
                }}</span>
              </div>

              <div class="order-status-rules__select-input">
                <SelectableField
                  v-if="isEditing"
                  :value="getOperatorDisplayValue(condition.operator)"
                  :selected-id="condition.operator"
                  :suggestions="conditionOperatorSuggestions"
                  @update-value="(e) => updateConditionOperator(ruleIndex, conditionIndex, getOperatorByDisplayValue(e))"
                />
                <span v-else class="order-status-rules__readonly-text">{{ getOperatorDisplayValue(condition.operator) }}</span>
                <span v-if="isEditing" class="order-status-rules__error-text">{{ '　' }}</span>
              </div>

              <div class="order-status-rules__value-cell" :class="{ 'order-status-rules__value-cell--readonly': !isEditing }">
                <span v-if="!isEditing" class="order-status-rules__readonly-text">{{ getConditionValueDisplayValue(condition) }}</span>
                <div v-else-if="isNoValueOperator(condition.operator)" class="order-status-rules__no-value">--</div>
                <template v-else>
                  <div class="order-status-rules__select-input">
                    <SelectableField
                      :value="getValueTypeDisplayValue(condition.valueType)"
                      :selected-id="`${condition.id}-value-type`"
                      :suggestions="getConditionValueTypeSuggestions(condition.operator)"
                      @update-value="(e) => updateConditionValueType(ruleIndex, conditionIndex, e)"
                    />
                  </div>
                  <input
                    v-if="condition.valueType === 'number'"
                    :value="condition.value ?? ''"
                    class="order-status-rules__input"
                    type="number"
                    :placeholder="t('入力してください')"
                    @change="(e) => updateNumberConditionValue(ruleIndex, conditionIndex, (e.target as HTMLInputElement).value)"
                  />
                  <ConditionDateInput
                    v-else-if="condition.valueType === 'date'"
                    :model-value="condition.value"
                    @update:model-value="(value) => updateConditionByIndex(ruleIndex, conditionIndex, { value })"
                  />
                  <input
                    v-else
                    :value="condition.value ?? ''"
                    class="order-status-rules__input"
                    type="text"
                    :placeholder="t('入力してください')"
                    @change="
                      (e) => updateConditionByIndex(ruleIndex, conditionIndex, { value: (e.target as HTMLInputElement).value })
                    "
                  />
                </template>
                <span v-if="isEditing" class="order-status-rules__error-text">{{ getConditionValueErrorMessage(condition) }}</span>
              </div>

              <button
                v-if="isEditing"
                type="button"
                class="order-status-rules__icon-button"
                :aria-label="t('条件削除')"
                @click="removeCondition(ruleIndex, conditionIndex)"
              >
                <Mdi icon="close" />
              </button>
            </div>
          </div>

          <div class="order-status-rules__result-cell">
            <OrderStatusFormSelectInput
              v-if="isEditing"
              label=""
              :model-value="rule.result"
              :index="ruleIndex"
              :options="SELECTABLE_ORDER_STATUS"
              @update:model-value="(e) => updateRuleResult(ruleIndex, e)"
            />
            <OrderStatusBadge v-else :order-status-str="rule.result" />
          </div>

          <div class="order-status-rules__actions">
            <button
              v-if="isEditing"
              type="button"
              class="order-status-rules__icon-button"
              :aria-label="t('ルール削除')"
              @click="removeRule(ruleIndex)"
            >
              <Mdi icon="delete" variant="outlined" />
            </button>
          </div>

          <button v-if="isEditing" type="button" class="order-status-rules__add-condition-button" @click="addCondition(ruleIndex)">
            <Mdi icon="add" />
            {{ t('条件行を追加') }}
          </button>
        </div>
      </div>
      <div
        v-if="isEditing"
        class="order-status-rules__end-drop-line"
        :class="{ 'order-status-rules__end-drop-line--active': isDragOverEnd }"
        @dragover="handleDragOverEnd"
        @dragleave="handleDragLeaveEnd"
        @drop="handleDropToEnd"
      />

      <button v-if="isEditing" type="button" class="order-status-rules__add-rule-button" @click="addRule">
        <Mdi icon="add" />
        {{ t('ルールを追加') }}
      </button>
    </div>

    <div class="order-status-fallback">
      {{ t('条件に一致しないものは全て') }}
      <OrderStatusFormSelectInput
        v-if="isEditing"
        label=""
        :model-value="fallbackStatus"
        :index="FALLBACK_STATUS_SELECT_INDEX"
        :options="SELECTABLE_ORDER_STATUS"
        @update:model-value="updateFallbackStatus"
      />
      <OrderStatusBadge v-else :order-status-str="fallbackStatus" />
      {{ t('でシステムに登録する') }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.order-status-info {
  display: grid;
  grid-template-rows: auto auto auto;
  gap: 16px;
  min-height: 0;

  &__title {
    font-family: Noto Sans JP;
    font-size: $fontSizeM;
    font-weight: 700;
    color: $textDefault;
  }
}

.order-status-rules {
  --order-status-rules-result-width: 120px;
  --order-status-rules-actions-width: 32px;
  --order-status-rules-row-height: 32px;
  --order-status-rules-header-height: 42px;

  display: grid;
  align-content: start;
  grid-auto-rows: max-content;
  border-radius: 8px;
  border: 2px solid $borderDefault;
  width: 100%;
  min-height: 0;
  max-height: calc(var(--order-status-rules-header-height) + 150px);
  overflow-y: auto;

  &__rule {
    position: relative;
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    align-items: start;
    column-gap: 8px;
    padding: 8px;
    border-bottom: 1px solid $borderDefault;

    > .drag-handle {
      height: 100%;
      align-self: center;
    }

    &--header {
      position: sticky;
      top: 0;
      min-height: var(--order-status-rules-header-height);
      align-items: center;
      color: $textSecondary;
      font-size: $fontSizeS;
      font-weight: 700;
      background-color: $semanticWhite;
      z-index: 10;
    }

    &--dragging {
      opacity: 0.5;
    }

    &--drop-target {
      &::before {
        content: '';
        position: absolute;
        top: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: $semanticPrimary;
        z-index: 1;
        pointer-events: none;
      }
    }
  }

  &__rule-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) var(--order-status-rules-result-width) var(--order-status-rules-actions-width);
    align-items: stretch;
    column-gap: 8px;
    row-gap: 4px;
    min-width: 0;
  }

  &__condition-row {
    display: grid;
    grid-template-columns: 32px minmax(0, 5fr) minmax(0, 4fr) minmax(0, 5fr) 32px;
    align-items: center;
    column-gap: 8px;
    min-height: var(--order-status-rules-row-height);
    min-width: 0;

    &--header {
      min-height: auto;
    }
  }

  &__conditions {
    grid-column: 1;
    display: grid;
    row-gap: 4px;
  }

  &__select-input {
    display: grid;
    grid-template-rows: auto auto;
    min-width: 0;
    z-index: 2;
  }

  &__value-cell {
    display: grid;
    grid-template-columns: 80px minmax(0, 1fr);
    align-items: center;
    column-gap: 4px;
    min-width: 0;

    &--readonly {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  &__error-text {
    grid-column: 1 / -1;
    color: $semanticError;
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    font-weight: 400;
  }

  &__readonly-text {
    min-width: 0;
    overflow: hidden;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__join-label {
    color: $textSecondary;
    font-size: $fontSizeS;
    text-align: center;
  }

  &__no-value {
    color: $textDefault;
    font-size: $fontSizeS;
  }

  &__input {
    min-width: 0;
    height: var(--order-status-rules-row-height);
    padding: 5px 8px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
  }

  &__result-cell,
  &__result-header {
    grid-column: 2;
    align-self: center;
  }

  &__result-cell {
    display: grid;
    align-items: center;
    height: var(--order-status-rules-row-height);
    overflow: visible;

    :deep(.select-box__selected) {
      min-height: var(--order-status-rules-row-height);
      padding: 5px 8px;
    }
  }

  &__actions {
    grid-column: 3;
    align-self: center;
    display: grid;
    gap: 4px;
    justify-content: center;

    &--header {
      gap: 0;
    }
  }

  &__icon-button {
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: transparent;
    color: $textSecondary;
    cursor: pointer;
  }

  &__add-rule-button,
  &__add-condition-button {
    display: grid;
    grid-template-columns: max-content max-content;
    align-items: center;
    column-gap: 4px;
    border: none;
    background: transparent;
    color: $textSecondary;
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    cursor: pointer;
  }

  &__add-condition-button {
    grid-column: 1 / -1;
    justify-content: start;
    width: fit-content;
    margin-left: 64px;
    padding: 4px 0;
  }

  &__add-rule-button {
    justify-content: center;
    width: 100%;
    padding: 8px 4px;
    text-align: center;
  }

  &__end-drop-line {
    position: relative;
    height: 12px;
    margin-bottom: -12px;
    z-index: 1;

    &--active {
      &::before {
        content: '';
        position: absolute;
        top: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: $semanticPrimary;
        z-index: 1;
        pointer-events: none;
      }
    }
  }
}

.order-status-fallback {
  display: grid;
  grid-template-columns: repeat(3, max-content);
  align-items: center;
  padding: 8px;
}
</style>
